import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, paymentAPI } from '../services/api';
import {
  Users, Wifi, Tv, CreditCard, RefreshCw, UserPlus,
  AlertTriangle, Clock, MessageSquare
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n));

export default function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [growth, setGrowth] = useState([]);
  const [activity, setActivity] = useState(null);
  const [paySummary, setPaySummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [ov, gr, ac, ps] = await Promise.all([
          dashboardAPI.getOverview(),
          dashboardAPI.getMonthlyGrowth(),
          dashboardAPI.getRecentActivity(),
          paymentAPI.getSummary(),
        ]);
        setOverview(ov.data.data);
        setGrowth(gr.data.data);
        setActivity(ac.data.data);
        setPaySummary(ps.data.data);
      } catch (err) {
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div className="loading-screen" style={{ height: 300 }}><div className="spinner" /></div>;
  if (!overview) return null;

  const statCards = [
    { label: 'Total Customers', value: overview.customers.total, icon: Users, color: '#2563eb', bg: '#dbeafe', sub: `${overview.customers.active} active` },
    { label: 'Broadband', value: overview.customers.broadband, icon: Wifi, color: '#0891b2', bg: '#cffafe', sub: 'active connections' },
    { label: 'CATV', value: overview.customers.catv, icon: Tv, color: '#7c3aed', bg: '#ede9fe', sub: 'active subscribers' },
    { label: 'This Month Revenue', value: `₹${fmt(overview.revenue.thisMonth)}`, icon: CreditCard, color: '#059669', bg: '#d1fae5', sub: `${overview.revenue.growth >= 0 ? '+' : ''}${overview.revenue.growth}% vs last month`, trend: overview.revenue.growth >= 0 ? 'up' : 'down' },
    { label: 'Expiring in 7 Days', value: overview.renewals.in7days, icon: RefreshCw, color: '#d97706', bg: '#fef3c7', sub: `${overview.renewals.today} today` },
    { label: 'Open Complaints', value: overview.complaints.open, icon: MessageSquare, color: '#dc2626', bg: '#fee2e2', sub: `${overview.complaints.critical} critical` },
    { label: 'Outstanding Dues', value: `₹${fmt(overview.outstanding)}`, icon: AlertTriangle, color: '#ea580c', bg: '#ffedd5', sub: 'total pending' },
    { label: 'Expired Accounts', value: overview.renewals.expired, icon: Clock, color: '#64748b', bg: '#f1f5f9', sub: 'need renewal' },
    { label: 'New Customers (CATV)', value: overview.customers.newCatvThisMonth, icon: UserPlus, color: '#7c3aed', bg: '#ede9fe', sub: 'added this month' },
    { label: 'New Customers (Broadband)', value: overview.customers.newBroadbandThisMonth, icon: UserPlus, color: '#0891b2', bg: '#cffafe', sub: 'added this month' },
  ];

  return (
    <div>
      {/* Stat Cards */}
      <div className="stats-grid mb-6">
        {statCards.map((card) => (
          <div key={card.label} className="stat-card">
            <div className="stat-icon" style={{ background: card.bg }}>
              <card.icon size={20} color={card.color} />
            </div>
            <div className="stat-info">
              <p>{card.label}</p>
              <h3>{card.value}</h3>
              {card.sub && <div className={`trend ${card.trend || ''}`}>{card.sub}</div>}
            </div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid-2 mb-6">
        {/* Revenue trend */}
        <div className="card">
          <div className="card-header">
            <h3>Revenue Trend (6 Months)</h3>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={growth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => [`₹${fmt(v)}`, 'Revenue']} />
                <Line type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* New customers trend */}
        <div className="card">
          <div className="card-header">
            <h3>New Customers (6 Months)</h3>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={growth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="newCustomers" fill="#06b6d4" radius={[4, 4, 0, 0]} name="New Customers" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Today's collection + recent activity */}
      <div className="grid-2 mb-6">
        <div className="card">
          <div className="card-header">
            <h3>Today's Collection</h3>
            <Link to="/payments" className="btn btn-outline btn-sm">View All</Link>
          </div>
          <div className="card-body">
            <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--success)', marginBottom: 12 }}>
              ₹{fmt(paySummary?.today?.amount || 0)}
            </div>
            <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: 16 }}>
              {paySummary?.today?.count || 0} transactions today
            </p>
            {paySummary?.methodBreakdown?.map(m => (
              <div key={m._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--border)', fontSize: '.85rem' }}>
                <span style={{ textTransform: 'capitalize' }}>{m._id?.replace('_', ' ')}</span>
                <span style={{ fontWeight: 600 }}>₹{fmt(m.total)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Recent Payments</h3>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>Method</th>
                </tr>
              </thead>
              <tbody>
                {activity?.recentPayments?.map(p => (
                  <tr key={p._id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{p.customer?.name}</div>
                      <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{p.customer?.customerId}</div>
                    </td>
                    <td style={{ fontWeight: 600, color: 'var(--success)' }}>₹{fmt(p.totalAmount)}</td>
                    <td><span className="badge badge-info" style={{ textTransform: 'capitalize' }}>{p.paymentMethod}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  );
}
